/**
 * CLEARO announcement rotator
 *
 * Rotates announcement-bar messages automatically. Pauses on hover, while a
 * message link has keyboard focus, and while the tab is in the background.
 * Transitions are handled in CSS (assets/clearo.css), including the
 * reduced-motion variant.
 */

// How long each message stays on screen before the next one appears.
const ROTATION_INTERVAL_MS = 3500;

class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.announcement-rotator__item'));
    if (this.items.length < 2) return;

    this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
    this.hovered = false;
    this.focused = false;
    this.editorLocked = false;

    this.onMouseEnter = () => {
      this.hovered = true;
      this.stop();
    };
    this.onMouseLeave = () => {
      this.hovered = false;
      this.start();
    };
    this.onFocusIn = () => {
      this.focused = true;
      this.stop();
    };
    this.onFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.focused = false;
      this.start();
    };
    this.onVisibilityChange = () => (document.hidden ? this.stop() : this.start());
    this.onBlockSelect = (event) => {
      const selected = this.items.indexOf(event.target);
      if (selected === -1) return;
      this.editorLocked = true;
      this.stop();
      this.show(selected);
    };
    this.onBlockDeselect = (event) => {
      if (!this.items.includes(event.target)) return;
      this.editorLocked = false;
      this.start();
    };

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  start() {
    this.stop();
    if (this.hovered || this.focused || this.editorLocked || document.hidden) return;
    this.timer = setInterval(() => this.show((this.index + 1) % this.items.length), ROTATION_INTERVAL_MS);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  show(nextIndex) {
    if (nextIndex === this.index) return;

    const current = this.items[this.index];
    const next = this.items[nextIndex];

    current.classList.remove('is-active');
    current.classList.add('is-leaving');
    current.setAttribute('aria-hidden', 'true');
    current.inert = true;
    current.addEventListener('transitionend', () => current.classList.remove('is-leaving'), { once: true });

    next.classList.remove('is-leaving');
    next.classList.add('is-active');
    next.removeAttribute('aria-hidden');
    next.inert = false;

    this.index = nextIndex;
  }
}

if (!customElements.get('announcement-rotator')) {
  customElements.define('announcement-rotator', AnnouncementRotator);
}
